<template>
  <div class="add-item-modal" v-if="showEditItem">
    <div v-click-outside="handleClose">
      <div v-if="isCurrentEditItemLoaded">
        <add-item-header :title="menuItemName" @close-modal="handleClose" />
        <div class="add-item-modal-body">
          <add-item-details
            :title="menuItemName"
            :description="menuItemDescription"
            :price="menuItemPrice"
            :imageURL="menuItemImageURL"
            :isAlcohol="menuItemIsAlcohol"
            :allergyIcons="menuItemAllergyIcons"
          />
          <div v-for="optionGroup in initOptionGroups" :key="optionGroup.id">
            <add-item-choices
              v-if="!optionGroup.parentID && !showNestedOptions"
              :nestedOptionGroups="initNestedOptionGroups"
              :title="optionGroup.name"
              :choices="optionGroup.options"
              :id="optionGroup.id"
              :name="optionGroup.name"
              :type="optionGroup.type"
              :atLeast="optionGroup.atLeast"
              :atMost="optionGroup.atMost"
              :required="optionGroup.required"
              :allNestedOptionsSelected="getAllNestedOptionsSelected"
              @chooseManyInput="handleChooseMany"
              @chooseOneInput="handleChooseOne"
              @editNestedSelections="handleEditNestedSelections"
            />
            <nested-add-item-choices
              v-else-if="showNestedOptions && optionGroup.parentID == currentNestedOptionGroups[0].parentID"
              :nestedOptionGroups="initNestedOptionGroups"
              :title="optionGroup.name"
              :choices="optionGroup.options"
              :id="optionGroup.id"
              :name="optionGroup.name"
              :type="optionGroup.type"
              :atLeast="optionGroup.atLeast"
              :atMost="optionGroup.atMost"
              :required="optionGroup.required"
              :parentID="optionGroup.parentID"
              :allNestedOptionsSelected="getAllNestedOptionsSelected"
              @chooseManyInput="handleChooseMany"
              @chooseOneInput="handleChooseOne"
              @closeNestedOptions="handleCloseNestedOptions"
              @saveNestedOptions="handleSaveNestedOptions"
              @editNestedSelections="handleEditNestedSelections"
            />
          </div>
          <add-item-who-for
            v-show="!showNestedOptions"
            v-model="personInstructions"
            :label="'Who\'s it for?'"
            :placeholder="'Name'"
            :specialInstructions="personInstructions"
          />
          <add-item-special-instructions
            v-show="!showNestedOptions"
            v-model="specInstructions"
            :label="'Special Instructions for ' + restName"
            :disabledLabel="'Special Instructions are disabled for ' + restName"
            :allowItemNotes="allowItemNotes"
            :placeholder="'Dressing on the side'"
            :specialInstructions="specInstructions"
          />
        </div>
        <div v-show="!showNestedOptions" class="add-item-footer">
          <div class="footer-items">
            <add-item-quantity v-model="itemQuantity" :initQuantity="itemQuantity" :itemName="menuItemName" />
            <yellow-button @submit-add-item="submitChanges" :text="`Save $${itemSubTotal}`" :showIcon="true" />
          </div>
        </div>
      </div>
      <div v-else-if="showSpinner">
        <div class="loading-header">
          <div class="loading-header-top">
            <button type="button" class="modal-close-button" @click.prevent="handleClose">
              <span class="screen-reader">Close Cart</span>
              <div class="icon-close">
                <div class="icon-close-inner"></div>
              </div>
            </button>
          </div>
        </div>
        <div class="modal-body loading">
          <i class="fas fa-spinner fa-spin"></i>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import vClickOutside from 'v-click-outside'

import AddItemHeader from '../../rest-menu-components/ModalItems/AddItemHeader.vue'
import AddItemDetails from '../../rest-menu-components/ModalItems/AddItemDetails.vue'
import AddItemWhoFor from '../../rest-menu-components/ModalItems/AddItemWhoFor.vue'
import AddItemSpecialInstructions from '../../rest-menu-components/ModalItems/AddItemSpecialInstructions.vue'
import YellowButton from '../../rest-menu-components/ModalItems/YellowButton.vue'
import AddItemQuantity from '../../rest-menu-components/ModalItems/AddItemQuantity.vue'
import AddItemChoices from '../../rest-menu-components/ModalItems/AddItemChoices.vue'
import NestedAddItemChoices from '../../rest-menu-components/ModalItems/NestedAddItemChoices.vue'

import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'EditItemModal',
  components: {
    AddItemDetails,
    AddItemHeader,
    AddItemWhoFor,
    AddItemSpecialInstructions,
    YellowButton,
    AddItemQuantity,
    AddItemChoices,
    NestedAddItemChoices,
  },
  directives: {
    clickOutside: vClickOutside.directive,
  },
  computed: {
    ...mapGetters({
      isCurrentEditItemLoaded: 'restList/isCurrentEditItemLoaded',
      currentEditItemObj: 'restList/currentEditItemObj',
      allowItemNotes: 'restList/allowItemNotes',
    }),
    showSpinner: function () {
      if (this.isCurrentEditItemLoaded || !this.showSpinnerStatus) {
        return false
      } else {
        return true
      }
    },
    getAllNestedOptionsSelected: function () {
      var allOptionsSelected = this.chooseManyChoices.concat(this.chooseOneChoices)
      var allNestedOptionsSelected = allOptionsSelected.filter((option) => option.parentID)
      return allNestedOptionsSelected
    },
    itemSubTotal: function () {
      var itemSubTotal = parseFloat(this.menuItemPrice)
      var allOptionsSelected = this.chooseManyChoices.concat(this.chooseOneChoices)
      allOptionsSelected.forEach((option) => {
        if (option.price) {
          itemSubTotal += parseFloat(option.price)
        }
      })
      itemSubTotal = itemSubTotal * this.itemQuantity
      return itemSubTotal.toFixed(2)
    },
  },
  props: {
    showEditItem: {
      type: Boolean,
      default: true,
    },
  },
  watch: {
    currentEditItemObj: {
      handler(oldVal, newVal) {
        this.restName = this.currentEditItemObj.restName
        this.restId = this.currentEditItemObj.restId
        this.menuItemName = this.currentEditItemObj.name
        this.menuItemDescription = this.currentEditItemObj.description
        this.menuItemPrice = this.currentEditItemObj.price
        this.initOptionGroups = this.currentEditItemObj.optionGroups
        this.initNestedOptionGroups = this.currentEditItemObj.nestedOptionGroups
        this.initChooseMany = this.currentEditItemObj.chooseManyOptions
        this.initChooseOne = this.currentEditItemObj.chooseOneOptions
        this.isDisabled = this.currentEditItemObj.isDisabled
        this.menuItemImageURL = this.currentEditItemObj.imageURL
        this.menuItemIsAlcohol = this.currentEditItemObj.isAlcohol
        this.menuItemAllergyIcons = this.currentEditItemObj.allergyIcons
        this.specInstructions = this.currentEditItemObj.specialInstructions
        this.personInstructions = this.currentEditItemObj.whoFor
        this.itemQuantity = this.currentEditItemObj.quantity
        let preselectedChooseManyOptions = []
        this.currentEditItemObj.chooseManyOptions.forEach((optionGroup) => {
          optionGroup.options.forEach((option) => {
            const returnOption = {
              optionGroupId: optionGroup.id,
              optionId: option.id,
              parentID: optionGroup.parentID,
              optionGroupName: optionGroup.name,
              optionName: option.name,
              price: option.price,
            }
            if (option.value) {
              preselectedChooseManyOptions.push(returnOption)
            }
          })
        })
        this.chooseManyChoices = preselectedChooseManyOptions
        let preselectedChooseOneOptions = []
        this.currentEditItemObj.chooseOneOptions.forEach((optionGroup) => {
          optionGroup.options.forEach((option) => {
            const returnOption = {
              optionGroupId: optionGroup.id,
              optionId: option.id,
              parentID: optionGroup.parentID,
              optionGroupName: optionGroup.name,
              optionName: option.name,
              price: option.price,
            }
            if (option.value) {
              preselectedChooseOneOptions.push(returnOption)
            }
          })
        })
        this.chooseOneChoices = preselectedChooseOneOptions
      },
    },
  },
  data() {
    return {
      menuItemName: '',
      menuItemDescription: '',
      menuItemPrice: 0,
      initOptionGroups: [],
      initNestedOptionGroups: {},
      initChooseMany: [],
      initChooseOne: [],
      specInstructions: '',
      personInstructions: '',
      itemQuantity: 1,
      chooseManyChoices: [],
      chooseOneChoices: [],
      isDisabled: false,
      menuItemImageURL: '',
      menuItemIsAlcohol: false,
      menuItemAllergyIcons: [],
      showSpinnerStatus: true,
      restName: '',
      restId: '',
      showNestedOptions: false,
      currentNestedOptionGroups: [],
    }
  },
  methods: {
    ...mapActions({
      addItemToCart: 'restList/addItemToCart',
      removeItem: 'restList/removeItem',
      updateShowCart: 'restList/updateShowCart',
      updateCurrentEditItemLoaded: 'restList/updateCurrentEditItemLoaded',
    }),
    handleClose() {
      this.$emit('close-modal')
      this.updateShowCart(true)
      this.$nextTick(function () {
        document.getElementById('close-cart').focus()
      })
    },
    handleCloseNestedOptions() {
      this.showNestedOptions = false
      this.currentNestedOptionGroups = []
    },
    handleSaveNestedOptions() {
      this.showNestedOptions = false
      this.currentNestedOptionGroups = []
    },
    handleEditNestedSelections(optionId) {
      this.showNestedOptions = true
      this.currentNestedOptionGroups = this.initNestedOptionGroups[optionId]
    },
    clearNestedOptions(parentID) {
      this.chooseOneChoices = this.chooseOneChoices.filter((option) => option.parentID != parentID)
      this.chooseManyChoices = this.chooseManyChoices.filter((option) => option.parentID != parentID)
      this.initOptionGroups.forEach((optionGroup) => {
        if (optionGroup.parentID == parentID) {
          optionGroup.options.forEach((option) => {
            option.value = false
            if (option.id in this.initNestedOptionGroups) {
              this.clearNestedOptions(option.id)
            }
          })
        }
      })
    },
    handleChooseOne(payload) {
      var chooseOneOptionGroupIndex = this.chooseOneChoices.findIndex((choice) => choice.optionGroupId === payload.optionGroupId)

      if (chooseOneOptionGroupIndex !== -1) {
        if (payload.optionId != this.chooseOneChoices[chooseOneOptionGroupIndex].optionId) {
          this.clearNestedOptions(this.chooseOneChoices[chooseOneOptionGroupIndex].optionId)
        }
        this.chooseOneChoices[chooseOneOptionGroupIndex] = payload
      } else {
        this.chooseOneChoices.push(payload)
      }

      var initChooseOneOptionGroupIndex = this.initOptionGroups.findIndex((optionGroup) => optionGroup.id === payload.optionGroupId)
      this.initOptionGroups[initChooseOneOptionGroupIndex].options.forEach((option) => {
        if (option.id === payload.optionId) {
          option.value = true
        } else {
          option.value = false
        }
      })
      // show nested options if there are any
      if (payload.hasNestedOptions) {
        this.showNestedOptions = true
        this.currentNestedOptionGroups = this.initNestedOptionGroups[payload.optionId]
      }
    },
    handleChooseMany(payload) {
      var initChooseManyIndex = this.initOptionGroups.findIndex((choice) => choice.id === payload.optionGroupId)
      var selectedChoicesInOptionGroupCount = this.initOptionGroups[initChooseManyIndex].options.filter((option) => option.value)

      var self = this
      var continueAddingOption = true
      this.initOptionGroups[initChooseManyIndex].options.forEach((option) => {
        if (option.id === payload.optionId) {
          if (!option.value) {
            if (
              self.initOptionGroups[initChooseManyIndex].atMost &&
              selectedChoicesInOptionGroupCount.length < self.initOptionGroups[initChooseManyIndex].atMost
            ) {
              option.value = true
            } else if (!self.initOptionGroups[initChooseManyIndex].atMost) {
              option.value = true
            } else {
              continueAddingOption = false
            }
          } else {
            option.value = false
          }
        }
      })

      if (!continueAddingOption) {
        return false
      }

      var chooseManyIndex = this.chooseManyChoices.findIndex((choice) => choice.optionId === payload.optionId)
      if (chooseManyIndex === -1) {
        this.chooseManyChoices.push(payload)
        if (payload.hasNestedOptions) {
          this.showNestedOptions = true
          this.currentNestedOptionGroups = this.initNestedOptionGroups[payload.optionId]
        }
      } else {
        this.clearNestedOptions(payload.optionId)
        this.chooseManyChoices = this.chooseManyChoices.filter((option) => option.optionId !== payload.optionId)
      }
    },
    isParentOptionRequiredOrSelected(parentID) {
      var allOptionGroups = this.initChooseMany.concat(this.initChooseOne)
      var allOptionsSelected = this.chooseManyChoices.concat(this.chooseOneChoices)
      var parentOptionGroup = allOptionGroups.find((optionGroup) => optionGroup.options.find((option) => option.id == parentID))
      var parentOptionSelected = allOptionsSelected.filter(function (choice) {
        return choice.optionId == parentID
      })
      var selectionMadeInParentOptionGroup = allOptionsSelected.filter(function (choice) {
        return choice.optionGroupId == parentOptionGroup.id
      })
      if ((parentOptionGroup.required && !selectionMadeInParentOptionGroup.length) || parentOptionSelected.length) {
        if (parentOptionGroup.parentID) {
          return this.isParentOptionRequiredOrSelected(parentOptionGroup.parentID)
        }
        return true
      }
      return false
    },
    async submitChanges() {
      var errors = []
      var errorMsg = ''
      this.updateCurrentEditItemLoaded(false)
      this.showSpinnerStatus = true
      if (this.currentEditItemObj.isDisabled) {
        this.updateCurrentEditItemLoaded(true)
        this.showSpinnerStatus = false
        alert('The menu that this item belongs to is not available at this time.')

        return false
      }
      for (var i = 0; i < this.initChooseMany.length; i++) {
        var optionGroup = this.initChooseMany[i]
        var parentIsRequiredOrSelected = true
        if (optionGroup.parentID) {
          parentIsRequiredOrSelected = this.isParentOptionRequiredOrSelected(optionGroup.parentID)
        }
        if (optionGroup.required && parentIsRequiredOrSelected) {
          var optionGroupSelected = this.chooseManyChoices.filter(function (choice) {
            return choice.optionGroupId == optionGroup.id
          })
          if (!optionGroupSelected.length) {
            errors.push('Please make a selection for ' + optionGroup.name)
          }
        }
        if (optionGroup.atLeast > 0 && parentIsRequiredOrSelected) {
          var optionGroupSelected = this.chooseManyChoices.filter(function (choice) {
            return choice.optionGroupId == optionGroup.id
          })
          if (optionGroupSelected.length < optionGroup.atLeast) {
            errors.push('Please select at least ' + optionGroup.atLeast + ' choices for ' + optionGroup.name)
          }
        }
        if (optionGroup.atMost > 0 && parentIsRequiredOrSelected) {
          var optionGroupSelected = this.chooseManyChoices.filter(function (choice) {
            return choice.optionGroupId == optionGroup.id
          })
          if (optionGroupSelected.length > optionGroup.atMost) {
            errors.push('Please select ' + optionGroup.atMost + ' or fewer choices for ' + optionGroup.name)
          }
        }
      }
      for (var i = 0; i < this.initChooseOne.length; i++) {
        var optionGroup = this.initChooseOne[i]
        var parentIsRequiredOrSelected = true
        if (optionGroup.parentID) {
          parentIsRequiredOrSelected = this.isParentOptionRequiredOrSelected(optionGroup.parentID)
        }
        if (optionGroup.required && parentIsRequiredOrSelected) {
          var optionGroupSelected = this.chooseOneChoices.filter(function (choice) {
            return choice.optionGroupId == optionGroup.id
          })
          if (!optionGroupSelected.length) {
            errors.push('Please make a selection for ' + optionGroup.name)
          }
        }
      }
      if (errors.length > 0) {
        this.updateCurrentEditItemLoaded(true)
        this.showSpinnerStatus = false
        for (var eachError in errors) {
          errorMsg += errors[eachError] + '\n'
        }
        alert(errorMsg)

        return false
      } else {
        var chooseOneOptionIds = this.chooseOneChoices.map((option) => option.optionId)
        var chooseManyOptionIds = this.chooseManyChoices.map((option) => option.optionId)
        var allOptionIds = chooseOneOptionIds.concat(chooseManyOptionIds)
        allOptionIds = allOptionIds.join('_')

        await this.removeItem({
          itemId: this.currentEditItemObj.id,
          restId: this.restId,
        })

        await this.addItemToCart({
          itemId: this.currentEditItemObj.id,
          qty: this.itemQuantity,
          options: allOptionIds,
          whofor: this.personInstructions,
          specInstructions: this.specInstructions,
          getCartData: true,
          markup: this.currentEditItemObj.markup,
        })
        this.$emit('close-modal')
        this.updateShowCart(true)
        this.$nextTick(function () {
          document.getElementById('close-cart').focus()
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.add-item-modal {
  position: fixed;
  border-radius: 20px;
  top: 5%;
  left: 50%;
  transform: translate(-50%, 0);
  width: 100%;
  max-width: 575px;
  z-index: 999;
  max-height: calc(100vh - 20%);
  @media (max-width: $mobileLandscape) {
    top: 3%;
  }
}

.add-item-modal-body {
  background-color: #ffffff;
  padding: 20px 40px 40px 40px;
  overflow: auto;
  height: 100%;
  max-height: 66vh;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.add-item-footer {
  background-color: #ffffff;
  padding: 20px 15px;
  border-top: 1px solid rgb(231, 231, 231) !important;
  position: sticky;
  bottom: 0;
  border-bottom-right-radius: 20px;
  border-bottom-left-radius: 20px;
  box-shadow: 0px -5px 20px 0px rgba(171, 168, 168, 0.5);
}

.footer-items {
  display: inline-flex;
  justify-content: space-around;
  width: 100%;
  align-items: center;
}

.fa-spinner {
  font-size: 100px;
  color: #f15925;
  opacity: 0.7;
}
.loading {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: $white;
  height: 500px;
  position: unset;
  border-bottom-right-radius: 20px;
  border-bottom-left-radius: 20px;
  @media (max-width: $tabletPortrait) {
    height: 300px;
  }
}

.loading-header {
  color: $black;
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
  width: 100%;
  background-color: $white;
  border-top-right-radius: 20px;
  border-top-left-radius: 20px;
}

.loading-header-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
}

.add-item-title {
  font-size: 2.8rem;
  font-weight: 900;
}

.screen-reader {
  @include screen-reader-text;
}

.modal-close-button {
  @include transition(color);
  color: $black;
  position: absolute;
  right: 3%;
  top: 3%;
  height: 40px;
  width: 40px;

  &:hover,
  &:focus {
    color: $gold;
  }
}

.icon-close {
  position: absolute;
  right: 50%;
  top: 0;
}

.icon-close-inner {
  &::before,
  &::after {
    content: '';
    display: block;
    background-color: currentColor;
    width: 6px;
    height: 33px;
    position: absolute;
    border-radius: 100px;

    @media (min-width: $mobileLandscape) {
      width: 6px;
      height: 53px;
    }
  }

  &::before {
    transform: translate(-50%, -3px) rotate(45deg);
  }

  &::after {
    transform: translate(-50%, -3px) rotate(-45deg);
  }
}
</style>
